/**
 * Simple OAuth2 service that works with the backend-driven login flow.
 *
 * The backend handles the full OAuth2 Authorization Code + PKCE dance.
 * The frontend just:
 * 1. Reads a one-time exchange code from the URL fragment (set by backend after successful login)
 * 2. Exchanges the code for an access token via a backend API call
 * 3. Stores the token in sessionStorage
 * 4. Redirects to the backend /oauth2/login endpoint when no token is available
 */

// Use the full backend URL (absolute) so that full-page redirects actually reach the backend,
// not the CRA dev server which would catch them with the React router.
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '/api';

// Tracks whether a token exchange is in progress. Other code (e.g. axios 401 interceptor)
// should wait for this before redirecting to login.
let tokenExchangePromise = null;

/**
 * Returns a promise that resolves when any in-progress token exchange completes.
 * Returns null if no exchange is in progress.
 */
export const getTokenExchangePromise = () => tokenExchangePromise;

/**
 * Check the URL fragment for a one-time exchange code (set by backend redirect after OAuth2 callback).
 * If found, exchange it for an access token via backend API, store in sessionStorage, and clean the URL.
 * Returns true if a token was successfully obtained.
 */
export const consumeTokenFromFragment = () => {
  if (isLogoutPage()) return Promise.resolve(false);

  const hash = window.location.hash;
  if (!hash) return Promise.resolve(false);

  const params = new URLSearchParams(hash.substring(1));
  const code = params.get('code');
  const authError = params.get('auth_error');

  // Clean URL immediately to prevent code reuse
  window.history.replaceState(null, '', window.location.pathname + window.location.search);

  if (authError) {
    console.error('[OAuth2] Authentication error:', authError);
    return Promise.resolve(false);
  }

  if (code) {
    tokenExchangePromise = (async () => {
      try {
        const response = await fetch(`${BACKEND_BASE}/oauth2/token?code=${encodeURIComponent(code)}`);
        if (response.ok) {
          const data = await response.json();
          sessionStorage.setItem('access_token', data.access_token);
          return true;
        }
        console.error('[OAuth2] Token exchange failed:', response.status);
      } catch (err) {
        console.error('[OAuth2] Token exchange error:', err);
      } finally {
        tokenExchangePromise = null;
      }
      return false;
    })();
    return tokenExchangePromise;
  }

  return Promise.resolve(false);
};

/**
 * Get the current access token from sessionStorage.
 */
export const getAccessToken = () => {
  return sessionStorage.getItem('access_token');
};

/**
 * Redirect to the backend OAuth2 login endpoint.
 * The backend will handle the full PKCE flow and redirect back with the token.
 */
export const login = () => {
  const returnUrl = window.location.pathname + window.location.search;
  const loginUrl = `${BACKEND_BASE}/oauth2/login?return_url=${encodeURIComponent(returnUrl)}`;
  window.location.href = loginUrl;
};

/**
 * Check if the current page is the logout page.
 */
export const isLogoutPage = () => {
  return window.location.pathname === '/logout';
};

/**
 * Clear the token and redirect to the backend logout endpoint,
 * which will also end the SSO session on the OAuth2 server.
 */
export const logout = () => {
  sessionStorage.removeItem('access_token');
  window.location.href = `${BACKEND_BASE}/oauth2/logout`;
};
